import type { Preferences } from '@teaplus/shared';

type ThemeId = Preferences['theme'];
type AccentId = Preferences['accent'];
type FontId = Preferences['font'];

/** Heading fonts: [Google Fonts family query, CSS font stack]. Outfit is loaded by index.html. */
export const FONTS: {
  id: FontId;
  label: string;
  kind: 'Serif' | 'Sans';
  query: string | null;
  stack: string;
}[] = [
  {
    id: 'outfit',
    label: 'Outfit',
    kind: 'Sans',
    query: null,
    stack: "'Outfit', system-ui, sans-serif",
  },
  {
    id: 'fraunces-classic',
    label: 'Fraunces',
    kind: 'Serif',
    query: 'Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700',
    stack: "'Fraunces', Georgia, serif",
  },
  {
    id: 'playfair',
    label: 'Playfair Display',
    kind: 'Serif',
    query: 'Playfair+Display:wght@500;600;700',
    stack: "'Playfair Display', Georgia, serif",
  },
  {
    id: 'dm-serif',
    label: 'DM Serif Display',
    kind: 'Serif',
    query: 'DM+Serif+Display',
    stack: "'DM Serif Display', Georgia, serif",
  },
  {
    id: 'lora',
    label: 'Lora',
    kind: 'Serif',
    query: 'Lora:wght@500;600;700',
    stack: "'Lora', Georgia, serif",
  },
  {
    id: 'baskerville',
    label: 'Libre Baskerville',
    kind: 'Serif',
    query: 'Libre+Baskerville:wght@400;700',
    stack: "'Libre Baskerville', Georgia, serif",
  },
  {
    id: 'space-grotesk',
    label: 'Space Grotesk',
    kind: 'Sans',
    query: 'Space+Grotesk:wght@500;600;700',
    stack: "'Space Grotesk', system-ui, sans-serif",
  },
  {
    id: 'sora',
    label: 'Sora',
    kind: 'Sans',
    query: 'Sora:wght@500;600;700',
    stack: "'Sora', system-ui, sans-serif",
  },
  {
    id: 'poppins',
    label: 'Poppins',
    kind: 'Sans',
    query: 'Poppins:wght@500;600;700',
    stack: "'Poppins', system-ui, sans-serif",
  },
  {
    id: 'bricolage',
    label: 'Bricolage Grotesque',
    kind: 'Sans',
    query: 'Bricolage+Grotesque:wght@500;600;700',
    stack: "'Bricolage Grotesque', system-ui, sans-serif",
  },
  {
    id: 'montserrat',
    label: 'Montserrat',
    kind: 'Sans',
    query: 'Montserrat:wght@500;600;700',
    stack: "'Montserrat', system-ui, sans-serif",
  },
];

/** Adds the Google Fonts stylesheet for these fonts once, so they can be shown. */
export function loadFonts(ids: FontId[]) {
  const wanted = FONTS.filter((f) => ids.includes(f.id) && f.query);
  for (const f of wanted) {
    const id = `font-${f.id}`;
    if (document.getElementById(id)) continue;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${f.query}&display=swap`;
    document.head.appendChild(link);
  }
}

/** [background, card] for the theme previews. Must match styles/index.css. */
export const THEMES: { id: ThemeId; label: string; colors: [string, string] }[] = [
  { id: 'graphite', label: 'Graphite', colors: ['#0a0a0b', '#19191c'] },
  { id: 'midnight', label: 'Midnight Blue', colors: ['#05070d', '#121828'] },
  { id: 'teaplus', label: 'Tea+ Night', colors: ['#08080d', '#171720'] },
  { id: 'plum', label: 'Plum', colors: ['#0c070d', '#1e1421'] },
  { id: 'ocean', label: 'Deep Ocean', colors: ['#041012', '#0f2328'] },
  { id: 'forest', label: 'Forest', colors: ['#060c08', '#121e16'] },
  { id: 'wine', label: 'Merlot', colors: ['#0e0609', '#221219'] },
  { id: 'espresso', label: 'Espresso', colors: ['#0d0907', '#1f1813'] },
];

/** [background gradient, card] for the gradient theme previews. Must match styles/index.css. */
export const GRADIENT_THEMES: { id: ThemeId; label: string; colors: [string, string] }[] = [
  {
    id: 'aurora',
    label: 'Aurora',
    colors: ['linear-gradient(155deg, #071317, #0c1326 48%, #160d23)', '#10202b'],
  },
  {
    id: 'sunset',
    label: 'Sunset',
    colors: ['linear-gradient(160deg, #180c17, #210e18 45%, #21130d)', '#26141d'],
  },
  {
    id: 'nebula',
    label: 'Nebula',
    colors: ['linear-gradient(150deg, #0a091d, #180c29 50%, #091626)', '#17132f'],
  },
  {
    id: 'lagoon',
    label: 'Lagoon',
    colors: ['linear-gradient(165deg, #061615, #081c24 50%, #0c1329)', '#0d2630'],
  },
  {
    id: 'rosewood',
    label: 'Rosewood',
    colors: ['linear-gradient(150deg, #180a11, #1c0d1b 50%, #110c1b)', '#25121e'],
  },
];

export const ACCENTS: { id: AccentId; label: string; color: string }[] = [
  { id: 'aqua', label: 'Aqua', color: '#8edfe0' },
  { id: 'pink', label: 'Pastel Pink', color: '#f4a7c5' },
  { id: 'lilac', label: 'Lilac', color: '#c9b4f5' },
  { id: 'blue', label: 'Pastel Blue', color: '#a7c8f4' },
  { id: 'mint', label: 'Mint', color: '#9fdcc4' },
  { id: 'sage', label: 'Sage', color: '#b9d3a1' },
  { id: 'butter', label: 'Butter', color: '#f3dc8f' },
  { id: 'peach', label: 'Peach', color: '#f6bf9e' },
  { id: 'coral', label: 'Coral', color: '#f5a08f' },
  { id: 'pearl', label: 'Pearl', color: '#e9e6f2' },
];

/** Gradient accents. Must match styles/index.css. */
export const GRADIENT_ACCENTS: { id: AccentId; label: string; color: string }[] = [
  { id: 'grad-candy', label: 'Candy', color: 'linear-gradient(120deg, #e4a7c1, #c2b1e6)' },
  {
    id: 'grad-sunrise',
    label: 'Sunrise',
    color: 'linear-gradient(120deg, #e3d097, #e5baa3 50%, #e4a197)',
  },
  { id: 'grad-ocean', label: 'Ocean', color: 'linear-gradient(120deg, #94d2d6, #a7c1e6)' },
  {
    id: 'grad-aurora',
    label: 'Aurora',
    color: 'linear-gradient(120deg, #a1d0c0, #94d2d6 45%, #c2b1e6)',
  },
  { id: 'grad-sorbet', label: 'Sorbet', color: 'linear-gradient(120deg, #e3d097, #e4a7c1)' },
];

type Hsl = [h: number, s: number, l: number];

function toHsl(hex: string): Hsl {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255) as [
    number,
    number,
    number,
  ];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === r
      ? ((g - b) / d + (g < b ? 6 : 0)) * 60
      : max === g
        ? ((b - r) / d + 2) * 60
        : ((r - g) / d + 4) * 60;
  return [h, s * 100, l * 100];
}

const hsl = ([h, s, l]: Hsl) => `hsl(${h.toFixed(1)} ${s.toFixed(1)}% ${l.toFixed(1)}%)`;

/** A dark palette in the hue of the chosen colour. Lightness is fixed so text always reads. */
export function customThemeVars(hex: string): Record<string, string> {
  const [h, s] = toHsl(hex);
  const sat = Math.min(s, 55);
  return {
    '--color-bg': hsl([h, sat * 0.7, 4]),
    '--color-bg-2': hsl([h, sat * 0.7, 6.5]),
    '--color-card': hsl([h, sat * 0.6, 10]),
    '--color-elevated': hsl([h, sat * 0.55, 13.5]),
    '--color-line': hsl([h, sat * 0.45, 19]),
  };
}

/** Very dark accents are lifted so pink-coloured text stays readable on the dark themes. */
export function customAccentVars(hex: string): Record<string, string> {
  const [h, s, l] = toHsl(hex);
  const base: Hsl = [h, s, Math.max(l, 55)];
  return {
    '--color-pink': hsl(base),
    '--color-pink-soft': hsl([h, s, Math.min(base[2] + 18, 92)]),
    '--color-pink-hover': hsl([h, s, base[2] - 8]),
    '--color-on-pink': hsl([h, Math.min(s, 60), base[2] > 72 ? 10 : 6]),
  };
}

/** A soft, dark background gradient from two colours, with surfaces tinted like the first. */
export function customGradientThemeVars([from, to]: [string, string]): Record<string, string> {
  const stop = (hex: string, l: number) => {
    const [h, s] = toHsl(hex);
    return hsl([h, Math.min(s, 55) * 0.8, l]);
  };
  return {
    ...customThemeVars(from),
    '--app-bg': `linear-gradient(155deg, ${stop(from, 10)}, ${stop(to, 14)})`,
  };
}

/** A gradient accent from two colours, lifted and softened so it reads on the dark themes. */
export function customGradientAccentVars([from, to]: [string, string]): Record<string, string> {
  const stop = (hex: string) => {
    const [h, s, l] = toHsl(hex);
    return hsl([h, Math.min(s, 70) * 0.85, Math.min(Math.max(l, 62), 82)]);
  };
  return {
    ...customAccentVars(from),
    '--accent-gradient': `linear-gradient(120deg, ${stop(from)}, ${stop(to)})`,
  };
}

const CUSTOM_VARS = [
  ...Object.keys(customGradientThemeVars(['#000000', '#000000'])),
  ...Object.keys(customGradientAccentVars(['#000000', '#000000'])),
];

export function applyAppearance(
  p: Pick<Preferences, 'theme' | 'accent' | 'customTheme' | 'customAccent'> &
    Partial<Pick<Preferences, 'font' | 'customThemeGradient' | 'customAccentGradient'>>,
) {
  const root = document.documentElement;
  const font = FONTS.find((f) => f.id === p.font) ?? FONTS[0]!;
  loadFonts([font.id]);
  if (font.id === 'outfit') root.style.removeProperty('--font-display');
  else root.style.setProperty('--font-display', font.stack);
  root.dataset.theme = p.theme;
  root.dataset.accent = p.accent;
  for (const v of CUSTOM_VARS) root.style.removeProperty(v);
  const vars = {
    ...(p.theme === 'custom' ? customThemeVars(p.customTheme) : {}),
    ...(p.accent === 'custom' ? customAccentVars(p.customAccent) : {}),
    ...(p.theme === 'custom-gradient' && p.customThemeGradient
      ? customGradientThemeVars(p.customThemeGradient)
      : {}),
    ...(p.accent === 'grad-custom' && p.customAccentGradient
      ? customGradientAccentVars(p.customAccentGradient)
      : {}),
  };
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
}
